import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { fetchReviews } from "../services/reviewsApi";

const ReviewsContext = createContext(null);

const CACHE_KEY = "einsured_reviews_cache_v1";
const CACHE_TTL_MS = 15 * 60 * 1000; // 15 min
const PER_PAGE = 15;

function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.ts || !Array.isArray(parsed?.data)) return null;
    if (Date.now() - parsed.ts > CACHE_TTL_MS) return null;
    return parsed.data;
  } catch {
    return null;
  }
}

function writeCache(data) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), data }));
  } catch {}
}

export function ReviewsProvider({ children }) {
  const cached = readCache();

  const cachedReviews = cached?.reviews || [];
  const cachedTotal = cached?.total || 0;

  const [reviews, setReviews] = useState(cachedReviews);
  const [total, setTotal] = useState(cachedTotal);
  const [loading, setLoading] = useState(!cachedReviews.length);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(cachedReviews.length ? 1 : 0);
  const [hasMore, setHasMore] = useState(true);

  async function loadFirstPage({ force = false } = {}) {
    setError("");

    if (!force) {
      const cached = readCache();
      if (cached?.reviews?.length) {
        setReviews(cached.reviews);
        setTotal(cached.total || 0);
        setPage(1);
        setHasMore(cached.reviews.length >= PER_PAGE);
        setLoading(false);
        return;
      }
    }

    setLoading(true);

    try {
      const data = await fetchReviews({ page: 1, perpage: PER_PAGE });

      setReviews(data.reviews);
      setTotal(data.total);
      setPage(1);
      setHasMore(data.hasMore);

      writeCache({
        reviews: data.reviews,
        total: data.total,
      });
    } catch (e) {
      setError(e?.message || "Failed to load reviews.");
    } finally {
      setLoading(false);
    }
  }

  async function loadMore() {
    if (loadingMore || loading || !hasMore) return;

    setLoadingMore(true);
    setError("");

    try {
      const nextPage = page + 1;
      const data = await fetchReviews({ page: nextPage, perpage: PER_PAGE });

      setReviews((prev) => {
        const existingIds = new Set(prev.map((item) => item.id));
        const merged = [...prev];

        data.reviews.forEach((item) => {
          if (!existingIds.has(item.id)) {
            merged.push(item);
          }
        });
        return merged;
      });

      setPage(nextPage);
      setHasMore(data.hasMore);
      setTotal(data.total);
    } catch (e) {
      setError(e?.message || "Failed to load more reviews.");
    } finally {
      setLoadingMore(false);
    }
  }

  async function refresh() {
    localStorage.removeItem(CACHE_KEY);
    await loadFirstPage({ force: true });
  }

  useEffect(() => {
    loadFirstPage({ force: false });
  }, []);

  const avg = useMemo(() => {
    if (!reviews.length) return 0;
    return reviews.reduce((s, r) => s + (r.rating || 0), 0) / reviews.length;
  }, [reviews]);

  const value = useMemo(
    () => ({
      reviews,
      total,
      loading,
      loadingMore,
      error,
      averageRating: avg,
      hasMore,
      refresh,
      loadMore,
    }),
    [reviews, loading, loadingMore, error, avg, hasMore]
  );

  return <ReviewsContext.Provider value={value}>{children}</ReviewsContext.Provider>;
}

export function useReviews() {
  const ctx = useContext(ReviewsContext);
  if (!ctx) throw new Error("useReviews must be used inside <ReviewsProvider>");
  return ctx;
}
